import React, { useCallback, useRef, useState } from 'react';
import cn from 'classnames';
import { useOnClickOutside } from 'utils/hooks/useOnClickOutside';
import { ReactComponent as Down } from 'style/imgs/icons/down.svg';
import styles from './styles.module.scss';

export interface Item<T = number> {
  id: T;
  label: string;
}

interface Props {
  items: Item[];
  value: Item | null;
  onChange(item: Item): void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

const Dropdown: React.FC<Props> = ({
  value,
  onChange,
  items,
  className,
  placeholder = 'Select item',
  disabled,
}) => {
  const [isOpen, setOpen] = useState(false);
  const toggleOpen = useCallback(() => setOpen((o) => !o), []);

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setOpen(false));

  const handleClick = useCallback(
    (item: Item) => {
      toggleOpen();
      onChange(item);
    },
    [onChange, toggleOpen]
  );

  return (
    <div ref={ref} className={cn(styles.root, className)}>
      <button
        type="button"
        className={cn(styles.value, { [styles.placeholder]: !value })}
        onClick={toggleOpen}
        disabled={disabled}
      >
        {value?.label ?? placeholder}
        <Down style={isOpen ? { transform: 'rotate(180deg)' } : undefined} />
      </button>
      {isOpen && (
        <ul className={styles.items}>
          {items.map((i) => (
            <div
              key={i.id}
              className={styles.item}
              role="presentation"
              onClick={() => handleClick(i)}
            >
              {i.label}
            </div>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
